import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, memo } from 'react'
import { Link } from 'react-router-dom'
import { productsService } from '../services/products'
import { useCartStore } from '../store/cartStore'
import {
  mod,
  getApiBaseUrl,
  buildImageSrc,
  getMainImageSrc,
  preloadImage,
  formatProductPrice,
  isImagePreloaded,
  sortProductSizes,
} from './productPageHelpers'

// Простая модель: один активный товар, переключение шагом (колесо / стрелки / свайп).
// Без скрытий/лоадеров — просто смена данных, чтобы не мигало изображение предыдущего товара.

const readVisualViewportBox = () => {
  if (typeof window === 'undefined') return null
  const vp = window.visualViewport
  if (vp) {
    return {
      top: vp.offsetTop,
      left: vp.offsetLeft,
      width: vp.width,
      height: vp.height,
    }
  }
  return {
    top: 0,
    left: 0,
    width: window.innerWidth,
    height: window.innerHeight,
  }
}

const styles = {
  page: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100dvh',
    maxHeight: '100dvh',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    overscrollBehavior: 'none',
    touchAction: 'none',
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    paddingTop: 'env(safe-area-inset-top, 0px)',
    minHeight: 'calc(52px + env(safe-area-inset-top, 0px))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: 'max(16px, env(safe-area-inset-left, 0px))',
    paddingRight: 'max(16px, env(safe-area-inset-right, 0px))',
    paddingBottom: 0,
    boxSizing: 'border-box',
    background: 'transparent',
    zIndex: 100,
    pointerEvents: 'none',
  },
  headerBtn: {
    pointerEvents: 'auto', background: 'none', border: 'none', cursor: 'pointer',
    color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '24px', height: '24px',
  },
  cartLink: { pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none', color: '#000' },
  cartCount: { fontSize: '12px', fontWeight: '500', color: '#000' },

  main: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 'calc(52px + env(safe-area-inset-top, 0px))',
    paddingLeft: 'max(16px, env(safe-area-inset-left, 0px))',
    paddingRight: 'max(16px, env(safe-area-inset-right, 0px))',
    paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px) + 36px)',
    boxSizing: 'border-box',
    overflowY: 'auto',
    overflowX: 'hidden',
    overscrollBehaviorY: 'none',
    overscrollBehaviorX: 'none',
    WebkitOverflowScrolling: 'touch',
    touchAction: 'pan-y',
  },
  mainBody: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  mainSpacer: {
    flex: '1 1 0',
    minHeight: 0,
    minWidth: 0,
  },
  mainContent: {
    flex: '0 0 auto',
    flexShrink: 0,
    width: '100%',
    maxWidth: '560px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '16px',
  },
  gallerySection: {
    width: '100%',
    flex: '0 0 auto',
    flexShrink: 0,
    aspectRatio: '3 / 4',
    maxHeight: 540,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  imageWrapper: {
    width: '100%',
    height: '100%',
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  imageLayer: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 220ms ease, transform 220ms ease',
    willChange: 'opacity, transform',
  },
  image: { maxWidth: '100%', maxHeight: '100%', width: '100%', height: '100%', objectFit: 'contain' },
  dots: { position: 'absolute', top: '16px', left: 0, width: '100%', display: 'flex', justifyContent: 'center', gap: '6px', zIndex: 10, pointerEvents: 'none' },
  dot: { width: '6px', height: '6px', borderRadius: '50%', background: '#ddd', transition: 'background 0.2s' },
  dotActive: { background: '#000' },
  arrowBtn: {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: '44px', height: '44px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.65)', border: 'none', cursor: 'pointer',
    zIndex: 10, color: '#000', borderRadius: '22px', boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
  },

  infoSection: {
    width: '100%',
    flex: '0 0 auto',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  info: {
    width: '100%',
    flexShrink: 0,
    boxSizing: 'border-box',
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '8px',
    backdropFilter: 'blur(10px)',
  },
  sku: { fontSize: '14px', fontWeight: '500', letterSpacing: '1px', textTransform: 'uppercase' },
  price: { fontSize: '14px', fontWeight: '400' },
  description: {
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: 1.4,
    color: '#333',
    textAlign: 'center',
    maxWidth: '100%',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  sizes: { display: 'flex', gap: '8px', marginTop: '4px' },
  sizeBtn: {
    minWidth: '44px', minHeight: '36px', padding: '4px 8px', border: '1px solid #ddd',
    fontSize: '11px', fontWeight: '500', fontFamily: 'Inter, sans-serif',
    background: '#fff', cursor: 'pointer', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: '0px',
  },
  recHeight: { fontSize: '9px', color: '#999', fontWeight: '400', lineHeight: '1', marginTop: '2px' },
  addBtn: {
    width: '48px', height: '48px', borderRadius: '0', border: 'none', background: 'transparent',
    fontSize: '48px', fontWeight: '400', lineHeight: '1', marginTop: '8px',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0', color: '#000', cursor: 'pointer',
  },
  loading: { height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  scrollHint: {
    position: 'absolute',
    bottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
    left: '50%',
    transform: 'translateX(-50%)',
    animation: 'bounce 2s infinite',
    zIndex: 50,
    pointerEvents: 'none',
  },
}

const INPUT_COOLDOWN_MS = 180

const useImageReady = (src) => {
  const [ready, setReady] = useState(() => isImagePreloaded(src))
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!src) { setReady(false); return }
    if (isImagePreloaded(src)) { setReady(true); return }

    const controller = new AbortController()
    const currentId = requestIdRef.current + 1
    requestIdRef.current = currentId

    preloadImage(src, controller.signal)
      .then((ok) => {
        if (requestIdRef.current !== currentId) return
        if (ok) setReady(true)
      })
      .catch(() => {
        /* игнорируем ошибки загрузки */
      })

    return () => {
      controller.abort()
    }
  }, [src])

  return ready
}

// ---------- ImageGallery ----------
const ImageGallery = memo(({ product, apiBaseUrl }) => {
  const [current, setCurrent] = useState(0)
  const [currentSrc, setCurrentSrc] = useState('')
  const [nextSrc, setNextSrc] = useState('')
  const [isFading, setIsFading] = useState(false)
  const touchRef = useRef({ x: null, y: null })
  const transitionIdRef = useRef(0)
  const activeTransitionRef = useRef(0)
  const productIdRef = useRef(product?.id)
  const [isPointerFine, setIsPointerFine] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(pointer: fine)').matches : true))

  const images = useMemo(() => {
    if (!product?.images) return []
    const main = product.images.find((i) => i.is_main) || product.images[0]
    const rest = product.images.filter((i) => i !== main)
    return [main, ...rest]
  }, [product?.images])

  const targetSrc = images[current] ? buildImageSrc(images[current], apiBaseUrl) : ''
  const isTargetReady = useImageReady(targetSrc)

  // Сброс состояния при смене товара
  useEffect(() => {
    if (productIdRef.current !== product?.id) {
      productIdRef.current = product?.id
      setCurrent(0)
      setCurrentSrc('')
      setNextSrc('')
      setIsFading(false)
      transitionIdRef.current = 0
      activeTransitionRef.current = 0
    }
  }, [product?.id])

  useEffect(() => {
    const mq = typeof window !== 'undefined' ? window.matchMedia('(pointer: fine)') : null
    if (!mq) return
    const handler = (e) => setIsPointerFine(e.matches)
    setIsPointerFine(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const hasMany = images.length > 1
  const showArrows = hasMany && isPointerFine

  const goPrev = useCallback(() => { if (hasMany) setCurrent((p) => (p - 1 + images.length) % images.length) }, [hasMany, images.length])
  const goNext = useCallback(() => { if (hasMany) setCurrent((p) => (p + 1) % images.length) }, [hasMany, images.length])

  const dots = hasMany && (
    <div style={styles.dots}>
      {images.map((_, i) => (
        <div key={i} style={{ ...styles.dot, ...(i === current ? styles.dotActive : {}) }} />
      ))}
    </div>
  )

  // Инициализация текущего кадра - приоритет готовому изображению
  useEffect(() => {
    if (!targetSrc) {
      setCurrentSrc('')
      return
    }
    
    // Если изображение уже готово и это первая загрузка или товар изменился
    if (isTargetReady && (!currentSrc || productIdRef.current === product?.id)) {
      // Если нет активного перехода, сразу устанавливаем изображение
      if (!isFading) {
      setCurrentSrc(targetSrc)
        setNextSrc('')
      }
    }
  }, [targetSrc, isTargetReady, product?.id])

  // Плавный переход: ждем decode нового src, затем кроссфейд
  useEffect(() => {
    if (!targetSrc) return
    if (targetSrc === currentSrc) {
      // Если уже отображается правильное изображение, сбрасываем состояние перехода
      if (isFading) {
        setIsFading(false)
        setNextSrc('')
      }
      return
    }
    
    // Ждем готовности изображения перед началом перехода
    if (!isTargetReady) return
    
    // Отменяем предыдущий переход, если он еще не завершился
    const transitionId = transitionIdRef.current + 1
    transitionIdRef.current = transitionId
    activeTransitionRef.current = transitionId
    
    // Если уже идет переход, отменяем его
    if (isFading && nextSrc) {
      // Завершаем предыдущий переход немедленно
      setCurrentSrc(nextSrc)
      setNextSrc('')
      setIsFading(false)
    }
    
    // Начинаем новый переход
    setNextSrc(targetSrc)
    const raf = requestAnimationFrame(() => {
      if (transitionIdRef.current === transitionId) setIsFading(true)
    })
    return () => cancelAnimationFrame(raf)
  }, [targetSrc, isTargetReady, currentSrc, isFading, nextSrc])

  const onTransitionEnd = useCallback((e) => {
    if (e.propertyName !== 'opacity') return
    if (!isFading || !nextSrc) return
    
    const currentTransitionId = activeTransitionRef.current
    // Проверяем, что это актуальный переход
    if (currentTransitionId !== transitionIdRef.current) return
    
    setCurrentSrc(nextSrc)
    setNextSrc('')
    setIsFading(false)
  }, [isFading, nextSrc])

  // свайп по горизонтали
  const onTouchStart = (e) => {
    touchRef.current.x = e.touches[0].clientX
    touchRef.current.y = e.touches[0].clientY
  }
  const onTouchEnd = (e) => {
    const sx = touchRef.current.x
    const sy = touchRef.current.y
    if (sx === null || sy === null) return
    const dx = sx - e.changedTouches[0].clientX
    const dy = sy - e.changedTouches[0].clientY
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
      dx > 0 ? goNext() : goPrev()
    }
    touchRef.current.x = null
    touchRef.current.y = null
  }

  // Fallback: если изображение не загрузилось, показываем targetSrc напрямую
  const displaySrc = currentSrc || targetSrc
  const displayNextSrc = nextSrc || (isFading ? targetSrc : '')

  return (
    <div
      style={{ position: 'relative', width: '100%', flex: '1 1 auto', minHeight: 0 }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div style={styles.imageWrapper}>
        {dots}
        {displaySrc && (
        <img
            key={`current-${product?.id}-${current}`}
            src={displaySrc}
            alt={product?.name || ''}
          style={{
            ...styles.image,
            ...styles.imageLayer,
            opacity: isFading ? 0 : 1,
            transform: `scale(${isFading ? 0.98 : 1})`,
            position: 'absolute',
            inset: 0,
          }}
          draggable="false"
            onError={() => {
              console.warn('Failed to load image:', displaySrc)
            }}
        />
        )}
        {displayNextSrc && displayNextSrc !== displaySrc && (
        <img
            key={`next-${product?.id}-${current}-${transitionIdRef.current}`}
            src={displayNextSrc}
            alt={product?.name || ''}
          style={{
            ...styles.image,
            ...styles.imageLayer,
            opacity: isFading ? 1 : 0,
            transform: `scale(${isFading ? 1 : 0.98})`,
            position: 'absolute',
            inset: 0,
          }}
          onTransitionEnd={onTransitionEnd}
          draggable="false"
            onError={() => {
              console.warn('Failed to load next image:', displayNextSrc)
              setIsFading(false)
              setNextSrc('')
            }}
        />
        )}
      </div>
      {showArrows && (
        <>
          <button type="button" onClick={goPrev} className="product-arrow-btn" style={{ ...styles.arrowBtn, left: '12px' }} aria-label="Предыдущее фото">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 19l-7-7 7-7" /></svg>
          </button>
          <button type="button" onClick={goNext} className="product-arrow-btn" style={{ ...styles.arrowBtn, right: '12px' }} aria-label="Следующее фото">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" /></svg>
          </button>
        </>
      )}
    </div>
  )
})

// ---------- Info ----------
const ProductInfo = memo(({ product, selectedVariant, onAddToCart, onSizeSelect, showSizes, addMessage }) => {
  if (!product) return null
  const getSku = (p) => p?.sku || `${(p?.name || '').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()}-${String(p?.id).padStart(2, '0')}`

  const sizes = useMemo(
    () => sortProductSizes((product.variants?.map((v) => v.size) ?? []).filter(Boolean)),
    [product.variants],
  )
  // Проверяем доступность: если есть выбранный вариант - проверяем его остаток
  // Если вариантов нет, проверяем остаток самого товара (если есть поле stock)
  // Если варианты есть, но ни один не выбран, проверяем есть ли хотя бы один доступный вариант
  const isAvailable = selectedVariant 
    ? selectedVariant.stock > 0 
    : (product.variants && product.variants.length > 0
        ? product.variants.some(v => v.stock > 0)
        : false) // Если нет вариантов, товар недоступен (предполагаем что остаток хранится в вариантах)
  const price = selectedVariant?.final_price || product.price
  const isAdded = !!addMessage

  return (
    <div style={styles.info} className="info-fade-enter-active">
      <div style={styles.sku}>{getSku(product)}</div>
      <div style={styles.price}>{formatProductPrice(price)} ₽</div>

      {product.description?.trim() ? (
        <div style={styles.description}>{product.description.trim()}</div>
      ) : null}

      {showSizes && sizes.length > 0 && (
        <div style={styles.sizes}>
          {sizes.map(size => {
            const variant = product.variants.find(v => v.size === size)
            const isDisabled = !variant || variant.stock === 0
            const isSelected = selectedVariant?.size === size
            return (
              <button
                type="button"
                key={size}
                onClick={(e) => { e.stopPropagation(); !isDisabled && onSizeSelect(product, size) }}
                disabled={isDisabled}
                style={{
                  ...styles.sizeBtn,
                  background: isSelected ? '#000' : '#fff',
                  color: isSelected ? '#fff' : '#000',
                  opacity: isDisabled ? 0.3 : 1,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  borderColor: isSelected ? '#000' : '#ddd',
                }}
              >
                <span>{size}</span>
                {variant?.recommended_height && (
                  <span style={{ ...styles.recHeight, color: isSelected ? 'rgba(255,255,255,0.7)' : '#999' }}>
                    {variant.recommended_height}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}

      {!isAvailable ? (
        <div style={{
          ...styles.addBtn,
          opacity: 0.5,
          fontSize: '9px',
          fontWeight: '600',
          letterSpacing: '1px',
          textTransform: 'uppercase',
          color: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'not-allowed',
          textAlign: 'center',
          width: '48px',
          margin: '8px auto 0',
        }}>
          SOLD OUT
        </div>
      ) : (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onAddToCart(product) }}
        disabled={!isAvailable}
        style={{
          ...styles.addBtn,
          opacity: isAvailable ? 1 : 0.3,
          cursor: isAvailable ? 'pointer' : 'not-allowed',
          position: 'relative',
        }}
      >
        <span style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: `translate(-50%, -50%) scale(${isAdded ? 0 : 1})`,
          opacity: isAdded ? 0 : 1, transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%',
        }}>+</span>
        <span style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: `translate(-50%, -50%) scale(${isAdded ? 1 : 0})`,
          opacity: isAdded ? 1 : 0, transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%',
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
      </button>
      )}
      {addMessage && <div style={{ fontSize: '12px', marginTop: '4px' }}>{addMessage}</div>}
    </div>
  )
})

// ---------- Page ----------
const ProductPage = () => {
  const [productId, setProductId] = useState(null)
  const handleBack = useCallback(() => {
    if (typeof window === 'undefined') return
    if (window.history.length > 1) window.history.back()
    else window.location.href = '/'
  }, [])
  const addItem = useCartStore((s) => s.addItem)
  const cartItemsCount = useCartStore((s) => s.getTotalItems())

  const [allProducts, setAllProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeIndex, setActiveIndex] = useState(0)
  const [selectedVariants, setSelectedVariants] = useState({})
  const [showSizes, setShowSizes] = useState(false)
  const [addMessage, setAddMessage] = useState('')
  const touchRef = useRef({ y: null, x: null })
  const lastInputRef = useRef(0)
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), [])
  const [vvBox, setVvBox] = useState(() =>
    typeof window !== 'undefined' ? readVisualViewportBox() : null,
  )

  // Высота/смещение по visualViewport — при появлении/скрытии панелей Safari на iOS не схлопываем вёрстку
  useLayoutEffect(() => {
    const sync = () => setVvBox(readVisualViewportBox())
    sync()
    window.addEventListener('resize', sync)
    const vp = window.visualViewport
    vp?.addEventListener('resize', sync)
    vp?.addEventListener('scroll', sync)
    return () => {
      window.removeEventListener('resize', sync)
      vp?.removeEventListener('resize', sync)
      vp?.removeEventListener('scroll', sync)
    }
  }, [])

  // iOS Safari/Chrome: убираем «резинку» и pull-to-refresh у всего документа при скролле контента
  useEffect(() => {
    if (typeof document === 'undefined') return
    const html = document.documentElement
    const body = document.body
    const root = document.getElementById('root')
    const prev = {
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      htmlOS: html.style.overscrollBehavior,
      bodyOverflow: body.style.overflow,
      bodyHeight: body.style.height,
      bodyOS: body.style.overscrollBehavior,
      rootHeight: root?.style.height ?? '',
      rootOverflow: root?.style.overflow ?? '',
      rootOS: root?.style.overscrollBehavior ?? '',
    }
    html.style.overflow = 'hidden'
    html.style.height = '100%'
    html.style.overscrollBehavior = 'none'
    body.style.overflow = 'hidden'
    body.style.height = '100%'
    body.style.overscrollBehavior = 'none'
    if (root) {
      root.style.height = '100%'
      root.style.overflow = 'hidden'
      root.style.overscrollBehavior = 'none'
    }
    return () => {
      html.style.overflow = prev.htmlOverflow
      html.style.height = prev.htmlHeight
      html.style.overscrollBehavior = prev.htmlOS
      body.style.overflow = prev.bodyOverflow
      body.style.height = prev.bodyHeight
      body.style.overscrollBehavior = prev.bodyOS
      if (root) {
        root.style.height = prev.rootHeight
        root.style.overflow = prev.rootOverflow
        root.style.overscrollBehavior = prev.rootOS
      }
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const parts = (window.location.pathname || '').split('/').filter(Boolean)
    const last = parts[parts.length - 1]
    const num = Number(last)
    if (Number.isFinite(num)) setProductId(num)
  }, [])

  useEffect(() => {
    let alive = true
    ;(async () => {
      setLoading(true)
      try {
        const data = await productsService.getAll()
        if (!alive) return
        const idx = productId != null ? data.findIndex((p) => p.id === Number(productId)) : -1
        const rotated = idx >= 0 ? [...data.slice(idx), ...data.slice(0, idx)] : data
        setAllProducts(rotated)
        setActiveIndex(0)
        const variants = {}
        data.forEach(p => {
          // Выбираем первый доступный вариант (с остатком > 0), если есть доступные
          // Если все варианты распроданы, выбираем первый вариант для отображения SOLD OUT
          if (p.variants && p.variants.length > 0) {
            const firstAvailable = p.variants.find(v => v.stock > 0) || p.variants[0]
            variants[p.id] = firstAvailable
          }
        })
        setSelectedVariants(variants)
      } catch (e) {
        console.error('Error fetching products:', e)
      } finally {
        setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [productId])

  const count = allProducts.length
  const activeProduct = allProducts[activeIndex] || allProducts[0]

  // Предзагрузка изображений: текущего товара и соседних
  useEffect(() => {
    if (!count || !activeProduct) return
    
    const targets = []
    
    // Предзагружаем все изображения текущего товара
    if (activeProduct.images && activeProduct.images.length > 0) {
      activeProduct.images.forEach(img => {
        const src = buildImageSrc(img, apiBaseUrl)
        if (src) targets.push(src)
      })
    }
    
    // Предзагружаем главные изображения соседних товаров
    const prevProduct = allProducts[mod(activeIndex - 1, count)]
    const nextProduct = allProducts[mod(activeIndex + 1, count)]
    const prevSrc = getMainImageSrc(prevProduct, apiBaseUrl)
    const nextSrc = getMainImageSrc(nextProduct, apiBaseUrl)
    if (prevSrc) targets.push(prevSrc)
    if (nextSrc) targets.push(nextSrc)
    
    // Предзагружаем с приоритетом: сначала текущий товар, потом соседние
    const timeouts = []
    targets.forEach((src, index) => {
      const delay = index < (activeProduct.images?.length || 0) ? 0 : 100
      timeouts.push(
        setTimeout(() => {
          preloadImage(src).catch(() => {})
        }, delay),
      )
    })
    return () => {
      timeouts.forEach(clearTimeout)
    }
  }, [allProducts, activeIndex, apiBaseUrl, count, activeProduct])

  const trigger = useCallback((dir) => {
    if (!count) return
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    if (now - lastInputRef.current < INPUT_COOLDOWN_MS) return
    lastInputRef.current = now
    setActiveIndex((p) => mod(p + dir, count))
  }, [count])

  useEffect(() => {
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) < 12) return
      e.preventDefault()
      trigger(e.deltaY > 0 ? 1 : -1)
    }
    const onKey = (e) => {
      if (e.key === 'ArrowDown') trigger(1)
      if (e.key === 'ArrowUp') trigger(-1)
    }
    window.addEventListener('wheel', onWheel, { passive: false })
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('wheel', onWheel)
      window.removeEventListener('keydown', onKey)
    }
  }, [trigger])

  useEffect(() => {
    const onStart = (e) => {
      touchRef.current.y = e.touches[0].clientY
      touchRef.current.x = e.touches[0].clientX
    }
    const onEnd = (e) => {
      const sy = touchRef.current.y
      const sx = touchRef.current.x
      if (sy === null || sx === null) return
      const dy = sy - e.changedTouches[0].clientY
      const dx = Math.abs(sx - e.changedTouches[0].clientX)
      if (Math.abs(dy) > dx && Math.abs(dy) > 50) {
        trigger(dy > 0 ? 1 : -1)
      }
      touchRef.current.y = null
      touchRef.current.x = null
    }
    window.addEventListener('touchstart', onStart, { passive: true })
    window.addEventListener('touchend', onEnd, { passive: true })
    return () => {
      window.removeEventListener('touchstart', onStart)
      window.removeEventListener('touchend', onEnd)
    }
  }, [trigger])

  const handleAddToCart = (product) => {
    const hasSizes = product.variants?.some(v => v.size)
    if (hasSizes && !showSizes) { setShowSizes(true); return }
    const variant = selectedVariants[product.id] || product.variants?.[0] || null
    
    // Проверяем доступность: если есть вариант - проверяем его остаток
    // Если вариантов нет или все варианты с остатком 0, не добавляем в корзину
    const hasVariants = product.variants && product.variants.length > 0
    if (hasVariants) {
      // Если есть варианты, проверяем остаток выбранного варианта
      if (!variant || variant.stock === 0) return
    } else {
      // Если нет вариантов, товар недоступен (остаток должен быть в вариантах)
      return
    }
    
    addItem(product, variant, 1)
    setAddMessage('Добавлено в корзину')
    setShowSizes(false)
    setTimeout(() => setAddMessage(''), 1500)
  }

  const handleSizeSelect = (product, size) => {
    const variant = product.variants?.find((v) => v.size === size)
    if (variant) setSelectedVariants((prev) => ({ ...prev, [product.id]: variant }))
  }

  const pageViewportStyle = vvBox
    ? {
        top: vvBox.top,
        left: vvBox.left,
        width: vvBox.width,
        maxWidth: vvBox.width,
        height: vvBox.height,
        maxHeight: vvBox.height,
        right: 'auto',
        bottom: 'auto',
      }
    : null

  const galleryMaxPx = vvBox ? Math.min(Math.round(vvBox.height * 0.62), 540) : 540

  if (loading) {
    return (
      <div
        style={{
          ...styles.loading,
          ...(pageViewportStyle
            ? {
                ...pageViewportStyle,
                position: 'fixed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }
            : {}),
        }}
      >
        <div className="spinner" />
      </div>
    )
  }
  if (!allProducts.length) {
    return (
      <div
        style={{
          ...styles.loading,
          ...(pageViewportStyle
            ? {
                ...pageViewportStyle,
                position: 'fixed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }
            : {}),
        }}
      >
        No products
      </div>
    )
  }

  return (
    <div style={{ ...styles.page, ...pageViewportStyle }}>
      <div style={styles.header}>
        <button type="button" onClick={handleBack} style={styles.headerBtn} aria-label="Назад">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <Link to="/cart" style={styles.cartLink} aria-label="Корзина">
          {cartItemsCount > 0 && <span style={styles.cartCount}>{cartItemsCount}</span>}
          <div style={{ ...styles.headerBtn, width: '24px', height: '24px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="5" y="11" width="14" height="10" rx="1" />
              <path d="M8 11V8a4 4 0 1 1 8 0v3" />
            </svg>
          </div>
        </Link>
      </div>

      <div style={styles.main}>
        <div style={styles.mainBody}>
          <div style={styles.mainSpacer} aria-hidden />
          <div style={styles.mainContent}>
            {activeProduct && (
              <div style={{ ...styles.gallerySection, maxHeight: galleryMaxPx }}>
                <ImageGallery product={activeProduct} apiBaseUrl={apiBaseUrl} />
              </div>
            )}
            <div style={styles.infoSection}>
              <ProductInfo
                product={activeProduct}
                selectedVariant={selectedVariants[activeProduct?.id]}
                onAddToCart={handleAddToCart}
                onSizeSelect={handleSizeSelect}
                showSizes={showSizes}
                addMessage={addMessage}
              />
            </div>
          </div>
          <div style={styles.mainSpacer} aria-hidden />
        </div>
      </div>

      <div style={styles.scrollHint} aria-hidden>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.4 }} aria-hidden>
          <path d="M12 5v14M5 12l7 7 7-7" />
        </svg>
      </div>
    </div>
  )
}

export default ProductPage
